import { useMemo } from "react";
import { CalendarClock, Inbox, Sparkles, Trophy } from "lucide-react";
import { ENQ_STATUSES, LEAD_SOURCES } from "@/lib/constants";
import { isDue } from "@/lib/leads";
import { Panel } from "@/components/admin/common";

const Tile = ({ icon: Icon, label, value, testId, onClick, active }) => (
  <button type="button" onClick={onClick} data-testid={testId} disabled={!onClick}
    className={`rounded-2xl border bg-white p-4 text-left transition-colors ${active ? "border-ocean ring-1 ring-ocean" : "border-slate-200"} ${onClick ? "hover:border-ocean" : "cursor-default"}`}>
    <Icon className="h-5 w-5 text-ocean" />
    <p className="mt-3 font-mono text-2xl font-semibold text-navy">{value}</p>
    <p className="text-xs text-slate-500">{label}</p>
  </button>
);

const Breakdown = ({ title, rows, testId }) => (
  <Panel className="p-4" data-testid={testId}>
    <p className="text-sm font-semibold text-navy">{title}</p>
    <div className="mt-3 flex flex-wrap gap-2">
      {rows.length === 0 ? <p className="text-xs text-slate-400">No data yet</p> : rows.map(([k, v]) => <span key={k} className="rounded-full bg-slate-100 px-3 py-1 text-xs text-slate-700">{k} <b className="font-mono">{v}</b></span>)}
    </div>
  </Panel>
);

const countBy = (leads, key, label = (x) => x) => Object.entries(leads.reduce((m, l) => {
  const k = label(l[key]) || "Not set";
  return { ...m, [k]: (m[k] || 0) + 1 };
}, {})).sort((a, b) => b[1] - a[1]);

export const LeadStats = ({ leads, dueOnly, setDueOnly }) => {
  const s = useMemo(() => {
    const month = new Date().toISOString().slice(0, 7);
    const booked = leads.filter((l) => l.status === "booked").length;
    return {
      month: leads.filter((l) => (l.created_at || "").startsWith(month)).length,
      due: leads.filter(isDue).length,
      conv: leads.length ? Math.round((booked / leads.length) * 100) : 0,
      bySource: countBy(leads, "source", (v) => LEAD_SOURCES.find((x) => x.value === v)?.label || "Website"),
      byTrip: countBy(leads, "trip_type"),
      byStage: ENQ_STATUSES.map((st) => [st.label, leads.filter((l) => l.status === st.value).length]),
    };
  }, [leads]);
  return (
    <div className="space-y-4 mb-4">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <Tile icon={Inbox} label="Total leads" value={leads.length} testId="leads-stat-total" />
        <Tile icon={Sparkles} label="Leads this month" value={s.month} testId="leads-stat-month" />
        <Tile icon={CalendarClock} label={dueOnly ? "Showing follow-ups due (tap to clear)" : "Follow-ups due"} value={s.due} testId="leads-stat-due" onClick={() => setDueOnly(!dueOnly)} active={dueOnly} />
        <Tile icon={Trophy} label="Lead to booking rate" value={`${s.conv}%`} testId="leads-stat-conversion" />
      </div>
      <div className="grid gap-3 lg:grid-cols-3">
        <Breakdown title="By stage" rows={s.byStage} testId="leads-by-stage" />
        <Breakdown title="By source" rows={s.bySource} testId="leads-by-source" />
        <Breakdown title="By requirement" rows={s.byTrip} testId="leads-by-trip" />
      </div>
    </div>
  );
};
